'use client';

import React, { useState } from 'react';
import { ChevronRight, ExternalLink, Check, X, AlertTriangle, Code, FileText, BarChart3 } from 'lucide-react';
import TrySeozastCTA from '@/app/components/TrySeozastCTA';

const TrackingScriptGuide = () => {
  const [activeSection, setActiveSection] = useState('');

  const platforms = [
    { id: 'webflow', name: 'Webflow', difficulty: 'Intermediate', scriptSupport: true },
    { id: 'wordpress', name: 'WordPress (Self-hosted)', difficulty: 'Easy', scriptSupport: true },
    { id: 'wordpresscom', name: 'WordPress.com', difficulty: 'Easy', scriptSupport: true },
    { id: 'shopify', name: 'Shopify', difficulty: 'Easy', scriptSupport: true },
    { id: 'wix', name: 'Wix', difficulty: 'Easy', scriptSupport: true },
    { id: 'notion', name: 'Notion', difficulty: 'Basic', scriptSupport: false },
    { id: 'framer', name: 'Framer', difficulty: 'Intermediate', scriptSupport: true },
    { id: 'custom', name: 'Custom HTML/Other', difficulty: 'Advanced', scriptSupport: true },
  ];

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
      case 'supported': return <Check className="w-4 h-4 text-emerald-600" />;
      case 'not-supported': return <X className="w-4 h-4 text-red-500" />;
      case 'workaround': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default: return null;
    }
  };

  const exampleTrackingScript = `<script async src="url/script.js" data-website-id="your-website-id-here"></script>`;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 bg-white">
      {/* Header */}
      <header className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Add Analytics Tracking to Your Platform
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Step-by-step guide to installing the tracking script for analytics on various web platforms
        </p>
        <div className="mt-6 text-sm text-gray-500">
          Updated: September 11, 2025 • By the SEOzast team
        </div>
      </header>

      {/* Table of Contents */}
      <div className="bg-gray-50 rounded-xl p-8 mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <FileText className="w-6 h-6 text-emerald-600 mr-3" />
          Table of Contents
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {platforms.map((platform) => (
            <button
              key={platform.id}
              onClick={() => scrollToSection(platform.id)}
              className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:border-emerald-500 hover:bg-emerald-50 ${
                activeSection === platform.id ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center">
                <span className="font-semibold text-gray-900">{platform.name}</span>
                <span className={`ml-3 px-2 py-1 text-xs rounded-full ${
                  platform.difficulty === 'Easy' ? 'bg-emerald-100 text-emerald-700' :
                  platform.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {platform.difficulty}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
          ))}
        </div>
      </div>

      {/* Quick Reference Table */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left font-semibold text-gray-900">Platform</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">Script Installation</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">Custom Code Support</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-900">Analytics Dashboard</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Webflow', script: 'supported', customCode: 'supported', dashboard: 'supported' },
                { name: 'WordPress', script: 'supported', customCode: 'supported', dashboard: 'supported' },
                { name: 'WordPress.com', script: 'supported', customCode: 'workaround', dashboard: 'supported' },
                { name: 'Shopify', script: 'supported', customCode: 'supported', dashboard: 'supported' },
                { name: 'Wix', script: 'supported', customCode: 'supported', dashboard: 'supported' },
                { name: 'Notion', script: 'not-supported', customCode: 'not-supported', dashboard: 'not-supported' },
                { name: 'Framer', script: 'supported', customCode: 'supported', dashboard: 'supported' },
                { name: 'Custom HTML', script: 'supported', customCode: 'supported', dashboard: 'supported' },
              ].map((row, idx) => (
                <tr key={idx} className="border-t border-gray-200">
                  <td className="px-6 py-4 font-medium text-gray-900">{row.name}</td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.script} /></td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.customCode} /></td>
                  <td className="px-6 py-4 text-center"><StatusIcon status={row.dashboard} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-600" />
            <span>Supported</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span>With limitations</span>
          </div>
          <div className="flex items-center gap-2">
            <X className="w-4 h-4 text-red-500" />
            <span>Not supported</span>
          </div>
        </div>
      </div>

      {/* Platform Sections */}
      <div className="space-y-16">
        {/* Webflow */}
        <section id="webflow" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Webflow</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Intermediate</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Script supported</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 text-emerald-600 mr-2" />
              Add Tracking Script
            </h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to Site Settings → Custom Code
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Paste the tracking script in the Head Code section:
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Save and publish your site
              </li>
            </ol>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto mt-4">
              {exampleTrackingScript}
            </pre>
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
              <p className="text-sm text-emerald-800">✅ Analytics will start collecting data after publishing</p>
            </div>
          </div>
        </section>

        {/* WordPress */}
        <section id="wordpress" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">WordPress (Self-hosted)</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Full support</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Using a Plugin (Recommended)</h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Install a plugin like "Insert Headers and Footers"
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Go to Settings → Insert Headers and Footers
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Paste the tracking script in the Header section
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                  Save changes
                </li>
              </ol>
              <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                <p className="text-sm text-emerald-800">✅ No coding required, works with most themes</p>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Code className="w-5 h-5 text-gray-600 mr-2" />
                Manual Theme Edit
              </h3>
              <ol className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                  Go to Appearance → Theme File Editor
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                  Edit header.php and add the script before the closing &lt;/head&gt; tag
                </li>
                <li className="flex items-start">
                  <span className="bg-gray-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                  Save changes
                </li>
              </ol>
              <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                <p className="text-sm text-yellow-800">⚠️ Use a child theme to avoid losing changes on theme updates</p>
              </div>
            </div>
          </div>
        </section>

        {/* WordPress.com */}
        <section id="wordpresscom" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">WordPress.com</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Limited access</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Tracking Script</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to Tools → Marketing → Traffic
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Scroll to "Custom Code" section
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Paste the tracking script in the Head section
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                Save changes
              </li>
            </ol>
            <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
              <p className="text-sm text-yellow-800">⚠️ Requires a Business plan or higher for custom code</p>
            </div>
          </div>
        </section>

        {/* Shopify */}
        <section id="shopify" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Shopify</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Script supported</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Tracking Script</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to Online Store → Themes
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Click Actions → Edit Code
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Open theme.liquid and paste the script before the closing &lt;/head&gt; tag
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                Save and preview changes
              </li>
            </ol>
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
              <p className="text-sm text-emerald-800">✅ Tracks all store pages automatically</p>
            </div>
          </div>
        </section>

        {/* Wix */}
        <section id="wix" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Wix</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Easy</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Script supported</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Tracking Script</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to Settings → Advanced → Custom Code
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Click Add Custom Code → Paste script in the Head section
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Select "All Pages" and "Load code once"
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                Save and publish
              </li>
            </ol>
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
              <p className="text-sm text-emerald-800">✅ Easy setup with Wix's built-in tools</p>
            </div>
          </div>
        </section>

        {/* Notion */}
        <section id="notion" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Notion</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">Basic</span>
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Not supported</span>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <X className="w-5 h-5 text-red-500 mr-2" />
              Tracking Limitations
            </h3>
            <p className="text-gray-700 mb-4">
              Notion does not support adding custom JavaScript tracking scripts directly to pages.
            </p>
            <p className="text-gray-700">
              To track analytics, consider hosting your Notion content on a platform like Super.so or Potion.so, then add the tracking script to those platforms.
            </p>
            <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
              <p className="text-sm text-yellow-800">⚠️ Requires third-party hosting for analytics</p>
            </div>
          </div>
        </section>

        {/* Framer */}
        <section id="framer" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Framer</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">Intermediate</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Script supported</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Add Tracking Script</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Go to Site Settings → Custom Code
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Paste the tracking script in the Start of &lt;head&gt; tag section
              </li>
              <li className="flex items-start">
                <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Publish your site
              </li>
            </ol>
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
              <p className="text-sm text-emerald-800">✅ Tracks all pages after publishing</p>
            </div>
          </div>
        </section>

        {/* Custom HTML/Other */}
        <section id="custom" className="scroll-mt-8">
          <div className="border-l-4 border-emerald-500 pl-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Custom HTML/Other</h2>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">Advanced</span>
              <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">Script supported</span>
            </div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Code className="w-5 h-5 text-blue-600 mr-2" />
              Manual Script Integration
            </h3>
            <p className="text-gray-700 mb-4">
              For custom-built websites or platforms with HTML access, add the tracking script manually.
            </p>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                Open your website’s HTML source code
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                Paste the tracking script before the closing &lt;/head&gt; tag
              </li>
              <li className="flex items-start">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                Save and deploy your changes
              </li>
            </ol>
            <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto mt-4">
              {exampleTrackingScript}
            </pre>
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
              <p className="text-sm text-emerald-800">✅ Works with any platform that allows HTML edits</p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 mt-16 p-6 rounded-lg shadow-sm text-center">
          <h3 className="text-lg font-semibold mb-4">Ready to start tracking?</h3>
          <TrySeozastCTA />
        </div>
      </div>
    </div>
  );
};

export default TrackingScriptGuide;